/**
 * Unauthorized Error Handler
 * Handles 401 Unauthorized errors by clearing authentication data and redirecting
 */

import { ApiError } from '@shared/services';

export interface UnauthorizedHandlerConfig {
  enableAutoRedirect?: boolean;
  redirectDelay?: number;
  clearStorage?: boolean;
  onUnauthorized?: () => void;
}

class UnauthorizedHandler {
  private static instance: UnauthorizedHandler;
  private config: UnauthorizedHandlerConfig;
  private isInitialized = false;
  private redirectTimeoutId: number | null = null;

  private constructor(config: UnauthorizedHandlerConfig = {}) {
    this.config = {
      enableAutoRedirect: true,
      redirectDelay: 1000,
      clearStorage: true,
      ...config
    };
  }

  public static getInstance(config?: UnauthorizedHandlerConfig): UnauthorizedHandler {
    if (!UnauthorizedHandler.instance) {
      UnauthorizedHandler.instance = new UnauthorizedHandler(config);
    }
    return UnauthorizedHandler.instance;
  }

  public initialize(): void {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;
    console.log('[UnauthorizedHandler] Initialized');
  }

  public destroy(): void {
    if (!this.isInitialized) {
      return;
    }

    // Clear any pending redirect animation frame
    if (this.redirectTimeoutId !== null) {
      cancelAnimationFrame(this.redirectTimeoutId);
      this.redirectTimeoutId = null;
    }

    this.isInitialized = false;
    console.log('[UnauthorizedHandler] Destroyed');
  }

  public handleUnauthorized(error: any): boolean {
    if (this.isUnauthorizedError(error)) {
      console.warn('[UnauthorizedHandler] 401 Unauthorized error detected:', error);
      this.clearAuthenticationData();
      this.handleRedirect();
      return true;
    }
    return false;
  }

  private static readonly unauthorizedPredicates: Array<(error: any) => boolean> = [
    (error) => error instanceof ApiError && error.status === 401,
    (error) => typeof error === 'object' && error !== null && error.status === 401,
    (error) => typeof error === 'object' && error !== null &&
      UnauthorizedHandler.messageIndicatesUnauthorized(error.message || error.statusText || ''),
    (error) => typeof error === 'string' && UnauthorizedHandler.messageIndicatesUnauthorized(error),
  ];

  private static readonly UNAUTHORIZED_KEYWORDS = [
    'Unauthorized',
    'Invalid authentication token',
    'Authentication token provided',
  ];

  private static messageIndicatesUnauthorized(message: string): boolean {
    return UnauthorizedHandler.UNAUTHORIZED_KEYWORDS.some(keyword => message.includes(keyword));
  }

  private isUnauthorizedError(error: any): boolean {
    return UnauthorizedHandler.unauthorizedPredicates.some((predicate) => predicate(error));
  }

  private clearStateFromUrl(): void {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.get('state')) return;
    urlParams.delete('state');
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }

  private clearAuthenticationData(): void {
    if (!this.config.clearStorage) return;
    try {
      localStorage.clear();
      sessionStorage.clear();
      this.clearStateFromUrl();
      console.log('[UnauthorizedHandler] Authentication data cleared');
    } catch (error) {
      console.warn('[UnauthorizedHandler] Failed to clear storage:', error);
    }
  }

  private performRedirect(): void {
    window.location.href = window.location.origin + window.location.pathname;
    this.redirectTimeoutId = null;
  }

  private scheduleDelayedRedirect(): void {
    if (this.redirectTimeoutId !== null) {
      cancelAnimationFrame(this.redirectTimeoutId);
    }
    const targetTime = performance.now() + (this.config.redirectDelay || 0);
    const check = () => {
      if (performance.now() >= targetTime) {
        this.performRedirect();
      } else {
        this.redirectTimeoutId = requestAnimationFrame(check);
      }
    };
    this.redirectTimeoutId = requestAnimationFrame(check);
  }

  private handleRedirect(): void {
    if (this.config.onUnauthorized) {
      this.config.onUnauthorized();
      return;
    }
    if (!this.config.enableAutoRedirect) return;

    const hasDelay = this.config.redirectDelay && this.config.redirectDelay > 0;
    if (hasDelay) {
      this.scheduleDelayedRedirect();
    } else {
      this.performRedirect();
    }
  }

  public updateConfig(newConfig: Partial<UnauthorizedHandlerConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

export const unauthorizedHandler = UnauthorizedHandler.getInstance();
export { UnauthorizedHandler };
export default UnauthorizedHandler;
